import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ClipLoader from 'react-spinners/ClipLoader'
import Link from 'next/link'
import HidePasswordToggle from '@components/HidePasswordToggle'
import { AuthContext } from '../../contexts/AuthContext'
import { getSession, signIn, signOut } from 'next-auth/react'
import { HistoryContext } from '../../contexts/HistoryContext'
import router, { useRouter } from 'next/router'

const SKIP_PATHS = [
  '/account/check-verification',
  '/account/register/bidder',
  '/account/forgot-password',
  '/account/reset-password',
  '/sso-logout',
]

const Login = () => {
  const router = useRouter()
  const [serverError, setServerError] = useState<any>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const { path }: any = useContext(HistoryContext)
  const { logEvent, userData } = useContext<any>(AuthContext)
  const checkSession = async () => {
    const session = await getSession()
    if (!session && userData.profile.id) {
      signOut({ redirect: false })
    }
  }

  useEffect(() => {
    checkSession()
  }, [])

  const callbackUrl =
    path?.prev &&
    typeof path?.prev === 'string' &&
    !SKIP_PATHS.some((PATH) => path.prev.includes(PATH))
      ? path.prev
      : '/account/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  useEffect(() => {
    if (userData.profile.id) {
      setSubmitting(false)
      router.push(callbackUrl)
    }
  }, [userData])

  const onSubmit = async (formData: any) => {
    setSubmitting(true)
    try {
      const options = { ...formData, redirect: false }
      const signInResponse = await signIn(`credentials`, options)
      if (signInResponse?.status === 200) {
        setServerError('')
        logEvent('Logged In')
      } else {
        throw { message: signInResponse?.error }
      }
    } catch (e: any) {
      console.log('ERROR', e)
      const error = e.response?.data || e
      setSubmitting(false)
      const {
        message = 'Sorry Something Went Wrong. Please Contact Our Support Team.',
      } = error
      const displayMsg =
        message === 'Bad credentials' ||
        message === 'Device type cannot be null' ||
        message.includes(
          "Couldn't find a matching user email in the database for"
        ) ? (
          'The username or password you have entered does not match an account we have on file.'
        ) : message.includes('This account is currently suspended') ? (
          <>
            {message.replace('information.', 'information at:')}{' '}
            <a href="mailto:support@bidfta.com">support@bidfta.com</a>.
          </>
        ) : (
          message
        )
      setServerError(displayMsg)
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`bg-white p-6 flex flex-col gap-11 rounded-lg border-2 border-bidfta-gray-border w-full max-w-md`}
    >
      <div className="">
        <label
          className="block text-gray-700 font-semibold text-xs"
          htmlFor="email"
        >
          E-mail Address / Bidder ID
        </label>
        <input
          className="border border-gray-400 p-2 rounded-md w-full font-semibold"
          id="username"
          autoComplete="username"
          {...register('username', { required: true })}
        />
        {errors.email && (
          <div className="text-red p-3 text-sm">
            Email or Bidder ID is missing
          </div>
        )}
      </div>
      <div className="">
        <label
          className="text-gray-700 font-semibold text-xs flex flex-row justify-between"
          htmlFor="password"
        >
          <p>Password</p>
          <HidePasswordToggle
            isVisible={isPasswordVisible}
            toggle={() => setIsPasswordVisible(!isPasswordVisible)}
          />
        </label>
        <input
          className="border border-gray-400 p-2 rounded-md w-full font-semibold"
          type={isPasswordVisible ? '' : 'password'}
          id="password"
          autoComplete="current-password"
          {...register('password', { required: true })}
        />
        {errors.password && (
          <div className="text-red p-1 text-sm">Password is missing</div>
        )}
      </div>
      <div>
        <label>
          {serverError && (
            <p className="text-red text-sm mt-2">{serverError}</p>
          )}
        </label>
        <button
          type="submit"
          disabled={submitting}
          className=" flex justify-center items-center text-center font-bold rounded-md px-4 border bg-bidfta-blue-light border-bidfta-blue-light text-white
                hover:text-bidfta-blue-light hover:border-bidfta-blue-light
                focus:border-bidfta-blue-light hover:bg-white focus:text-bidfta-blue-light
                focus:bg-white w-full py-2.5 text-10 md:text-xs transition duration-300 ease-in-out"
        >
          {submitting ? (
            <ClipLoader loading={submitting} size={25} color={'#FFDF1B'} />
          ) : (
            'Login'
          )}
        </button>
      </div>

      <p className="text-bidfta-blue-light text-center w-full text-sm">
        <Link href="/account/forgot-password" className="underline font-bold">
          Forgot Password?
        </Link>
      </p>
    </form>
  )
}

export default Login
